/**
 * 리스트를 센티넬 요소가 뷰포트에 들어올 때 점진 확장하는 무한스크롤 훅.
 *
 * - `items` identity 가 바뀌면(필터/검색/정렬 결과) visibleCount 를 `pageSize` 로 리셋.
 * - `sentinelRef` 를 리스트 말미 div 에 붙이면 IntersectionObserver 가 관찰한다.
 * - `rootMargin` 을 약간 크게 잡아 (기본 200px) "끝에 닿기 전에 미리 로드" 체감을 제공.
 *
 * /blocks, /packs, /services 등 카드 그리드 페이지가 동일한 UX 를 공유하도록 공통화.
 */
import { useEffect, useMemo, useRef, useState } from "react"

interface UseInfiniteScrollOptions {
	readonly pageSize?: number
	readonly rootMargin?: string
}

interface UseInfiniteScrollResult<T> {
	readonly visible: ReadonlyArray<T>
	readonly sentinelRef: React.RefObject<HTMLDivElement | null>
	readonly hasMore: boolean
	readonly loadedCount: number
	readonly totalCount: number
}

export function useInfiniteScroll<T>(
	items: ReadonlyArray<T>,
	opts: UseInfiniteScrollOptions = {}
): UseInfiniteScrollResult<T> {
	const pageSize = opts.pageSize ?? 20
	const rootMargin = opts.rootMargin ?? "200px"
	const [visibleCount, setVisibleCount] = useState(pageSize)
	const sentinelRef = useRef<HTMLDivElement | null>(null)

	// items reference 가 바뀌면 리셋 — filtered = useMemo(..., [deps]) 가 보통 새 array 를 리턴하므로
	// "검색어/카테고리/정렬" 변경 시 자동으로 처음부터 다시 쌓인다.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pageSize 변화는 reset 대상이 아님
	useEffect(() => {
		setVisibleCount(pageSize)
	}, [items])

	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		if (visibleCount >= items.length) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setVisibleCount((v) => Math.min(v + pageSize, items.length))
				}
			},
			{ rootMargin }
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [items.length, visibleCount, pageSize, rootMargin])

	const visible = useMemo(() => items.slice(0, visibleCount), [items, visibleCount])
	return {
		visible,
		sentinelRef,
		hasMore: visibleCount < items.length,
		loadedCount: visible.length,
		totalCount: items.length
	}
}
